import React, { useEffect, useMemo, useState } from 'react'
import {
  Table,
  CardBody,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody
} from 'reactstrap'
import Modal from 'react-bootstrap/Modal'
import { Spinner } from 'react-bootstrap'
import { API_URL } from './utils/Constants/URLS'
import { useParams } from 'react-router-dom'

/* ─── inline styles for the animated warning banner ─── */
const warningBannerStyle = `
  @keyframes pulseWarning {
    0%   { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.55); }
    60%  { box-shadow: 0 0 0 10px rgba(245, 158, 11, 0); }
    100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
  }
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .invoice-warning-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    border: 1.5px solid #f59e0b;
    border-left: 5px solid #d97706;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
    animation: pulseWarning 2s ease-in-out infinite, fadeSlideIn 0.4s ease-out both;
    font-size: 0.875rem;
    font-weight: 600;
    color: #92400e;
    letter-spacing: 0.01em;
  }
  .invoice-warning-banner .warn-icon {
    font-size: 1.2rem;
    flex-shrink: 0;
    animation: pulseWarning 2s ease-in-out infinite;
  }
`

// --- Helper: format money ---
const money = n =>
  (Number.isFinite(+n) ? Number(n) : 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

// derive buyerPos (state code) from GSTIN (first 2 characters)
const getBuyerPos = gstin =>
  typeof gstin === 'string' && gstin.length >= 2 ? gstin.slice(0, 2) : ''

// helper to derive consistent pick-list id
const getPickListId = (pl, idx) => String(pl._id ?? pl.id ?? `pl-${idx}`)

// Empty form — NO pre-fills, user must enter everything
const emptyForm = () => ({
  invoiceType: '',
  supplyType: '',
  buyerGstin: '',
  buyerPos: '',
  buyerLocation: '',
  buyerPin: '',
  shipToGstin: '',
  shipToLegalName: '',
  shipToAddress: '',
  shipToLocation: '',
  shipToPin: '',
  shipToStateCode: '',
  otherReferences: '',
  dispatchedThrough: '',
  dispatchDocumentNumber: '',
  vehicleNumber: '',
  // Export-specific fields (EXPWOP)
  destinationCountryCode: '',
  countryOfOrigin: '',
  portOfLoading: '',
  portOfDischarge: '',
  termsOfDelivery: '',
  letterOfCreditNo: '',
  letterOfCreditDate: '',
  piNo: '',
  piDate: '',
  termsOfPayment: '',
  detailsOfPayment: '',
  noOfPackages: '',
  grossWeight: '',
  netWeight: '',
  marking: '',
  customerBankDetails: '',
  currencyType: ''
})

const Invoice = () => {
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('token') : null

  const { storeid } = useParams()

  const [packListData, setPackListData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [openId, setOpenId] = useState('')
  const [btnLoading, setBtnLoading] = useState(false)
  const [creatingId, setCreatingId] = useState(null)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [plId, setPlId] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')

  // unified search state
  const [search, setSearch] = useState('')

  // store invoice form data per pick-list id
  const [invoiceForm, setInvoiceForm] = useState({})

  // edited narrations per pick-list → line item
  const [editedNarrations, setEditedNarrations] = useState({})

  const handleCloseDeclineModal = () => {
    setShowDeclineModal(false)
    setPlId('')
    setRejectionReason('')
  }

  const handleInvoiceChange = (id, field, value) => {
    setInvoiceForm(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }))
  }

  const handleNarrationChange = (pickListId, lineId, value) => {
    setEditedNarrations(prev => ({
      ...prev,
      [pickListId]: {
        ...(prev[pickListId] || {}),
        [lineId]: value
      }
    }))
  }

  const getLineNarration = (pickListId, lineId, fallback = '') => {
    const edited = editedNarrations[pickListId]?.[lineId]
    return edited !== undefined ? edited : fallback
  }

  const buildPickListLineItemsPayload = (pl, pickListFormId) => {
    const lineItems = Array.isArray(pl.lineItems) ? pl.lineItems : []
    return lineItems.map((li, idx) => {
      const lineId = String(li._id ?? `line-${idx}`)
      return {
        _id: li._id,
        model: li.model?._id ?? li.model,
        quantity: Number(li.quantity),
        listPrice: Number(li.listPrice ?? 0),
        discount: Number(li.discount ?? 0),
        rate: Number(li.rate ?? 0),
        gst: Number(li.gst ?? 18),
        narration: getLineNarration(pickListFormId, lineId, li.narration ?? '') || '',
        quotationPrice: Number(li.quotationPrice ?? li.rate ?? 0),
        standardLeadTime: li.standardLeadTime,
        committedDeliveryDate: li.committedDeliveryDate,
        value: Number(li.rate ?? 0) * Number(li.quantity ?? 0)
      }
    })
  }

  const updatePickListNarrations = async (pickList, pickListFormId) => {
    const plId = pickList._id ?? pickList.id
    const payload = {
      boxNumber: pickList.boxNumber ?? '',
      store: pickList.store?._id ?? pickList.store,
      lineItems: buildPickListLineItemsPayload(pickList, pickListFormId)
    }

    const res = await fetch(`${API_URL}pick-lists/${plId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    })

    const result = await res.json()
    if (!res.ok || result.error === true) {
      throw new Error(result.message || 'Failed to update pack list narrations')
    }

    setPackListData(prev =>
      prev.map(pl => {
        if (String(pl._id ?? pl.id) !== String(plId)) return pl
        const updatedLineItems = buildPickListLineItemsPayload(pickList, pickListFormId)
        return {
          ...pl,
          lineItems: (pl.lineItems || []).map((li, idx) => ({
            ...li,
            narration: updatedLineItems[idx]?.narration ?? li.narration
          }))
        }
      })
    )

    return result
  }

  useEffect(() => {
    const fetchPackLists = async () => {
      setLoading(true)
      setError(null)
      try {
        const params = new URLSearchParams({
          pagination: 'false',
          store: storeid,
        })
        if (search.trim()) params.append('salesOrderNumber', search.trim())

        const url = `${API_URL}pick-lists?${params.toString()}`
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : ''
          }
        })

        if (!res.ok) {
          const txt = await res.text()
          throw new Error(txt || `Status ${res.status}`)
        }

        const json = await res.json()

        const docs =
          json?.message?.docs ||
          json?.data?.docs ||
          json?.docs ||
          (Array.isArray(json) ? json : []) ||
          []

        setPackListData(docs)
      } catch (err) {
        console.error('Fetch pick-lists error:', err)
        setError(err.message || 'Failed to fetch pack lists')
        setPackListData([])
      } finally {
        setLoading(false)
      }
    }

    const debounceTimer = setTimeout(fetchPackLists, 400)
    return () => clearTimeout(debounceTimer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, storeid, search])

  // Initialize each pick-list with a completely empty form — no auto-fill
  useEffect(() => {
    if (!Array.isArray(packListData) || packListData.length === 0) return

    setInvoiceForm(prev => {
      const next = { ...prev }
      packListData.forEach((pl, idx) => {
        const id = getPickListId(pl, idx)
        if (next[id]) return // don't overwrite if user already started editing
        next[id] = emptyForm()
      })
      return next
    })
  }, [packListData])

  const updateStatus = async (e, pl_id, status) => {
    e.preventDefault()
    const url = `${API_URL}pick-lists/${pl_id}`
    try {
      const payload = { approvalStatus: status }
      if (status === 'rejected') {
        payload.rejectionReason = rejectionReason
      }

      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      })
      const result = await res.json()

      if (result.error === false) {
        if (status === 'rejected') {
          const pickList = packListData.find(
            pl => pl._id === pl_id || pl.id === pl_id
          )
          const deliveryInstructionId = pickList?.deliveryInstruction?._id

          if (deliveryInstructionId && pickList?.lineItems) {
            try {
              const diGetRes = await fetch(
                `${API_URL}delivery-instructions/${deliveryInstructionId}`,
                {
                  method: 'GET',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                  }
                }
              )

              if (!diGetRes.ok) {
                throw new Error(`Failed to fetch DI: ${diGetRes.status}`)
              }

              const diGetResult = await diGetRes.json()
              const currentDI = diGetResult?.message

              if (
                !Array.isArray(currentDI?.lineItems) ||
                currentDI.lineItems.length === 0
              ) {
                throw new Error('DI has no line items to update')
              }

              const modelAddBack = {}
              pickList.lineItems.forEach(plItem => {
                const modelId =
                  plItem?.model?._id ??
                  (typeof plItem?.model === 'string' ? plItem.model : null)
                if (modelId) {
                  modelAddBack[String(modelId)] =
                    (modelAddBack[String(modelId)] || 0) +
                    Number(plItem.quantity || 0)
                }
              })

              const updatedDILineItems = currentDI.lineItems
                .map(li => {
                  const modelId =
                    li?.model?._id ??
                    (typeof li?.model === 'string' ? li.model : null)

                  if (!modelId) {
                    console.warn('⚠️ Skipping line item with no model ID:', li)
                    return null
                  }

                  const currentQty = Number(li?.quantity ?? 0)
                  const addBack = modelAddBack[String(modelId)] || 0
                  const newQty = currentQty + addBack

                  return {
                    _id: li._id,
                    model: modelId,
                    quantity: newQty,
                    listPrice: Number(li?.listPrice ?? 0),
                    discount: Number(li?.discount ?? 0),
                    rate: Number(li?.rate ?? 0),
                    standardLeadTime: li?.standardLeadTime ?? null,
                    committedDeliveryDate: li?.committedDeliveryDate ?? null
                  }
                })
                .filter(item => item !== null)

              if (updatedDILineItems.length === 0) {
                throw new Error('No valid line items to update in DI')
              }

              const diPatchRes = await fetch(
                `${API_URL}delivery-instructions/${deliveryInstructionId}`,
                {
                  method: 'PATCH',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                  },
                  body: JSON.stringify({ lineItems: updatedDILineItems })
                }
              )

              const diPatchResult = await diPatchRes.json()

              if (!diPatchRes.ok) {
                throw new Error(
                  `PATCH failed: ${diPatchResult?.message || diPatchRes.status}`
                )
              }

              if (diPatchResult.error === false || diPatchRes.ok) {
                alert(`Pack list is ${status} and DI quantities reverted successfully`)
              } else {
                alert(`Pack list is ${status} but DI update may have failed`)
              }
            } catch (diError) {
              console.error('❌ Error reverting DI:', diError)
              alert(
                `Pack list is ${status} but failed to revert DI quantities: ${diError.message}`
              )
            }
          } else {
            alert(`Pack list is ${status}`)
          }
        } else {
          alert(`Pack list is ${status}`)
        }

        handleCloseDeclineModal()
      } else {
        alert('Failed to update Pack List')
      }
    } catch (e) {
      console.error('❌ Update status error:', e)
      alert('Oops!! Error occurred while updating pack list: ' + e.message)
    }
  }

  const toggleAccordion = id => {
    setOpenId(prev => (prev === id ? '' : id))
  }

  const formatDateTime = iso => {
    if (!iso) return '-'
    try {
      const d = new Date(iso)
      return d.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return iso
    }
  }

  const computeTotals = lineItems => {
    const items = Array.isArray(lineItems) ? lineItems : []
    const subTotal = items.reduce(
      (acc, li) => acc + (Number(li?.rate) || 0) * (Number(li?.quantity) || 0),
      0
    )
    return { subTotal }
  }

  const renderLines = (lines, id, canEditNarration = false) => {
    if (!Array.isArray(lines) || lines.length === 0) {
      return (
        <tr>
          <td colSpan='11' className='text-center py-3'>
            No line items
          </td>
        </tr>
      )
    }

    return lines.map((li, i) => {
      const qty = Number(li?.quantity || 0)
      const rate = Number(li?.rate || 0)
      const value = qty * rate
      const lineId = String(li._id ?? `line-${i}`)
      const narration = getLineNarration(id, lineId, li?.narration ?? '')
      return (
        <tr key={li._id ?? `${id}-li-${i}`}>
          <td>{i + 1}</td>
          <td>
            {li?.model?.modelName ?? '-'}
            {canEditNarration ? (
              <input
                type='text'
                className='form-control form-control-sm mt-1'
                placeholder='Enter narration'
                value={narration}
                onChange={e => handleNarrationChange(id, lineId, e.target.value)}
              />
            ) : (
              narration && (
                <>
                  <br />({narration})
                </>
              )
            )}
          </td>
          <td>{li?.model?.partCode ?? '-'}</td>
          <td>{li?.model?.HSNNumber ?? '-'}</td>
          <td>{li?.model?.uom ?? '-'}</td>
          <td>{qty}</td>
          <td>{money(li?.listPrice)}</td>
          <td>{`${Number(li?.discount || 0).toFixed(2)}%`}</td>
          <td>{li?.gst}</td>
          <td>{money(rate)}</td>
          <td>{money(value)}</td>
        </tr>
      )
    })
  }

  const summaryTotals = useMemo(() => {
    const allLines = packListData.flatMap(pl => {
      const li = Array.isArray(pl?.lineItems)
        ? pl.lineItems
        : Array.isArray(pl?.deliveryInstruction?.lineItems)
          ? pl.deliveryInstruction.lineItems
          : []
      return li
    })
    return computeTotals(allLines)
  }, [packListData])

  const createInvoice = async pickList => {
    if (!token) {
      alert('Missing auth token')
      return
    }

    const id = String(pickList._id ?? pickList.id ?? '')
    if (!id) {
      alert('Invalid pick list id')
      return
    }

    const form = invoiceForm[id] || {}
    const isExportInvoice = form.supplyType === 'EXPWOP'
    if (!form.invoiceType) {
      alert('Please select Invoice Type')
      return
    }
    if (!form.supplyType) {
      alert('Please select Supply Type')
      return
    }
    if (!isExportInvoice && !form.buyerGstin) {
      alert('Please enter Buyer GSTIN')
      return
    }
    if (!isExportInvoice && !form.buyerPos) {
      alert('Please enter Buyer POS (Place of Supply)')
      return
    }

    const payload = {
      pickListId: pickList._id ?? pickList.id,
      supplyType: form.supplyType === 'CRN' ? 'B2B' : form.supplyType || 'B2B',
      buyerGstin: isExportInvoice ? 'URP' : form.buyerGstin,
      buyerPos: isExportInvoice ? '96' : (form.buyerPos || getBuyerPos(form.buyerGstin)),
      buyerLocation: form.buyerLocation || '',
      buyerPin: form.buyerPin ? Number(form.buyerPin) : undefined,
      shipToGstin: isExportInvoice ? 'URP' : (form.shipToGstin || ''),
      shipToLegalName: form.shipToLegalName || '',
      shipToAddress: form.shipToAddress || '',
      shipToLocation: form.shipToLocation || '',
      shipToPin: form.shipToPin ? Number(form.shipToPin) : undefined,
      shipToStateCode: isExportInvoice ? '96' : (form.shipToStateCode || ''),
      otherReferences: form.otherReferences || '',
      dispatchedThrough: form.dispatchedThrough || '',
      dispatchDocumentNumber: form.dispatchDocumentNumber || '',
      vehicleNumber: form.vehicleNumber || '',
      isProformaInvoice: form?.invoiceType === 'PROFORMA' ? true : false,
    }

    if (form.supplyType === 'EXPWOP') {
      payload.destinationCountryCode = form.destinationCountryCode || ''
      payload.countryOfOrigin = form.countryOfOrigin || ''
      payload.portOfLoading = form.portOfLoading || ''
      payload.portOfDischarge = form.portOfDischarge || ''
      payload.termsOfDelivery = form.termsOfDelivery || ''
      payload.letterOfCreditNo = form.letterOfCreditNo || ''
      payload.letterOfCreditDate = form.letterOfCreditDate
        ? new Date(form.letterOfCreditDate).toISOString()
        : ''
      payload.piNo = form.piNo || ''
      payload.piDate = form.piDate
        ? new Date(form.piDate).toISOString()
        : ''
      payload.termsOfPayment = form.termsOfPayment || ''
      payload.detailsOfPayment = form.detailsOfPayment || ''
      payload.noOfPackages = form.noOfPackages || ''
      payload.grossWeight = form.grossWeight || ''
      payload.netWeight = form.netWeight || ''
      payload.marking = form.marking || ''
      payload.customerBankDetails = form.customerBankDetails || ''
      payload.currencyType = form.currencyType || ''
    }

    setBtnLoading(true)
    setCreatingId(id)

    try {
      await updatePickListNarrations(pickList, id)

      const url = form.supplyType === 'CRN'
        ? `${API_URL}credit-notes/create`
        : `${API_URL}invoices/create`

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        redirect: 'follow'
      })

      const text = await res.text()
      let parsed
      try {
        parsed = JSON.parse(text)
      } catch {
        parsed = text
      }

      if (!res.ok) {
        console.error('Create invoice error response:', parsed)
        alert(
          typeof parsed === 'string' ? parsed : (parsed?.message || JSON.stringify(parsed, null, 2))
        )
        return
      }

      try {
        const salesOrderId = pickList?.deliveryInstruction?.salesOrder?._id

        if (!salesOrderId) {
          console.warn('⚠️ No sales order ID found in picklist')
          alert('Invoice created successfully.')
          return
        }

        const soGetRes = await fetch(
          `${API_URL}leads/sales-orders/${salesOrderId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            }
          }
        )

        if (!soGetRes.ok) {
          throw new Error(`Failed to fetch Sales Order: ${soGetRes.status}`)
        }

        const soGetResult = await soGetRes.json()
        const currentSO = soGetResult?.data

        if (
          !Array.isArray(currentSO?.lineItems) ||
          currentSO.lineItems.length === 0
        ) {
          throw new Error('Sales Order has no line items to update')
        }

        const modelInvoiceQtyMap = {}
        const picklistLineItems = Array.isArray(pickList?.lineItems)
          ? pickList.lineItems
          : []

        picklistLineItems.forEach(plItem => {
          const modelId =
            plItem?.model?._id ??
            (typeof plItem?.model === 'string' ? plItem.model : null)
          if (modelId) {
            modelInvoiceQtyMap[String(modelId)] =
              (modelInvoiceQtyMap[String(modelId)] || 0) +
              Number(plItem.quantity || 0)
          }
        })

        const updatedSOLineItems = currentSO.lineItems.map(li => {
          const modelId =
            li?.model?._id ?? (typeof li?.model === 'string' ? li.model : null)

          if (!modelId) {
            return {
              _id: li._id,
              model: li.model,
              quantity: Number(li?.quantity ?? 0),
              listPrice: Number(li?.listPrice ?? 0),
              discount: Number(li?.discount ?? 0),
              rate: Number(li?.rate ?? 0),
              value: Number(li?.value ?? 0),
              standardLeadTime: li?.standardLeadTime ?? null,
              committedDeliveryDate: li?.committedDeliveryDate ?? null,
              diQuantity: Number(li?.diQuantity ?? 0),
              invoiceQuantity: Number(li?.invoiceQuantity ?? 0)
            }
          }

          const currentInvoiceQty = Number(li?.invoiceQuantity ?? 0)
          const addQty = modelInvoiceQtyMap[String(modelId)] || 0
          const newInvoiceQty = currentInvoiceQty + addQty

          return {
            _id: li._id,
            model: modelId,
            quantity: Number(li?.quantity ?? 0),
            listPrice: Number(li?.listPrice ?? 0),
            discount: Number(li?.discount ?? 0),
            rate: Number(li?.rate ?? 0),
            value: Number(li?.value ?? 0),
            standardLeadTime: li?.standardLeadTime ?? null,
            committedDeliveryDate: li?.committedDeliveryDate ?? null,
            diQuantity: Number(li?.diQuantity ?? 0),
            invoiceQuantity: newInvoiceQty
          }
        })

        const soPatchRes = await fetch(
          `${API_URL}leads/sales-orders/${salesOrderId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              lineItems: updatedSOLineItems,
              status: currentSO?.status
            })
          }
        )

        const soPatchResult = await soPatchRes.json()

        if (!soPatchRes.ok) {
          throw new Error(
            `PATCH failed: ${soPatchResult?.message || soPatchRes.status}`
          )
        }

        if (soPatchResult.error === false || soPatchRes.ok) {
          alert('Invoice created and Sales Order invoice quantities updated successfully')
        } else {
          alert('Invoice created but Sales Order update may have failed')
        }
      } catch (soError) {
        console.error('❌ Error updating Sales Order:', soError)
        alert(
          `Invoice created but failed to update Sales Order invoice quantities: ${soError.message}`
        )
      }
    } catch (err) {
      console.error('Create invoice error:', err)
      alert(err.message || String(err))
    } finally {
      setBtnLoading(false)
      setCreatingId(null)
    }
  }

  if (loading) {
    return (
      <div className='card shadow-sm'>
        <CardBody className='p-4 text-center'>Loading invoices...</CardBody>
      </div>
    )
  }

  if (error) {
    return (
      <div className='card shadow-sm'>
        <CardBody className='p-4'>
          <div className='alert alert-danger'>Error: {error}</div>
        </CardBody>
      </div>
    )
  }

  if (!packListData || packListData.length === 0) {
    // If no search filters active, show the simple empty state
    if (!search) {
      return (
        <div className='card shadow-sm'>
          <CardBody className='p-4 text-center'>No invoices found.</CardBody>
        </div>
      )
    }
    // Otherwise fall through to the main render so the search bar stays visible
  }

  return (
    <div className='card shadow-sm'>
      {/* Inject keyframe CSS once */}
      <style>{warningBannerStyle}</style>

      <CardBody className='p-4'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <h5 className='mb-0 ml-1'>Pack Lists</h5>
          <div className='text-muted small'>
            Total (all pack-lists):{' '}
            <strong>₹ {money(summaryTotals.subTotal)}</strong>
          </div>
        </div>

        {/* ── Search bar ── */}
        <div className='row g-2 mb-3'>
          <div className='col-md-10'>
            <div className='input-group input-group-sm'>
              <span className='input-group-text bg-white border-end-0'>
                <svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' fill='currentColor' viewBox='0 0 16 16'>
                  <path d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.099zm-5.242 1.156a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z' />
                </svg>
              </span>
              <input
                id='searchInput'
                type='text'
                className='form-control border-start-0'
                placeholder='Search by Sales Order No.'
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button
                  className='btn btn-outline-secondary border-start-0'
                  type='button'
                  onClick={() => setSearch('')}
                  title='Clear'
                >
                  &times;
                </button>
              )}
            </div>
          </div>
          <div className='col-md-2 d-flex align-items-center'>
            {search && (
              <button
                className='btn btn-sm btn-outline-danger w-100'
                onClick={() => setSearch('')}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <Accordion
          open={openId || ''}
          toggle={toggleAccordion}
          id='invoiceAccordion'
        >
          {(!packListData || packListData.length === 0) ? (
            <div className='text-center text-muted py-4'>
              <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' fill='currentColor' className='mb-2 opacity-50' viewBox='0 0 16 16'>
                <path d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.099zm-5.242 1.156a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z' />
              </svg>
              <div>No pack lists found matching your search.</div>
            </div>
          ) : packListData.map((pl, idx) => {
            const id = getPickListId(pl, idx)

            const createdAt = pl.createdAt ?? pl.updatedAt ?? null

            const lineItems = Array.isArray(pl.lineItems)
              ? pl.lineItems
              : Array.isArray(pl?.deliveryInstruction?.lineItems)
                ? pl.deliveryInstruction.lineItems
                : []

            const { subTotal } = computeTotals(lineItems)
            const form = invoiceForm[id] || {}
            const canEditNarration = pl?.status !== 'invoiced'

            return (
              <AccordionItem key={id}>
                <AccordionHeader targetId={id}>
                  <div className='d-flex flex-column flex-md-row align-items-start gap-2 w-100'>
                    <div className='me-auto'>
                      <strong>SO:</strong>{' '}
                      {pl?.deliveryInstruction?.salesOrderNumber}{' '}
                      <span className='text-muted'>
                        | Box: {pl?.boxNumber} | {' '}
                      </span>
                      <span className={pl?.status === 'invoiced' ? 'text-success' : 'text-primary'}>
                        Status: {pl?.status === 'invoiced' ? 'Invoiced' : 'Pending'}{' '}
                      </span>
                      |{' '}
                      {pl?.deliveryInstruction?.salesOrder?.client?.companyName}
                    </div>
                    <div className='text-muted small'>
                      Created: {formatDateTime(createdAt)}
                    </div>
                    <div className='ms-md-3'>
                      <span className='badge bg-light text-dark'>
                        Subtotal: ₹ {money(subTotal)}
                      </span>
                    </div>
                  </div>
                </AccordionHeader>

                <AccordionBody accordionId={id}>

                  {/* ===== Invoiced status: show summary only ===== */}
                  {pl?.status === 'invoiced' ? (
                    <div className='mb-3'>
                      <div className='row'>
                        <div className='col-md-3 mb-2'>
                          <strong>DI Number:</strong>
                          <div className='text-muted'>
                            {pl?.deliveryInstruction?.deliveryInstructionId || '-'}
                          </div>
                        </div>
                        <div className='col-md-3 mb-2'>
                          <strong>SO Number:</strong>
                          <div className='text-muted'>
                            {pl?.salesOrderNumber ||
                              pl?.deliveryInstruction?.salesOrderNumber ||
                              '-'}
                          </div>
                        </div>
                        <div className='col-md-3 mb-2'>
                          <strong>Box Number:</strong>
                          <div className='text-muted'>
                            {pl?.boxNumber || '-'}
                          </div>
                        </div>
                        <div className='col-md-3 mb-2'>
                          <strong>Customer PO Number:</strong>
                          <div className='text-muted'>
                            {pl?.customerPONumber ||
                              pl?.deliveryInstruction?.salesOrder?.customerPONumber ||
                              'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* ===== Approved: show full invoice form ===== */}
                      {pl?.approvalStatus === 'approved' && (
                        <div className='mb-3'>

                          {/* ── Animated warning banner ── */}
                          <div className='invoice-warning-banner'>
                            <span className='warn-icon'>⚠️</span>
                            <span>
                              Please check all details carefully before creation of invoice.
                            </span>
                          </div>

                          {/* ===== ROW 1 ===== */}
                          <div className='row'>
                            {/* Invoice Type */}
                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Invoice Type <span className='text-danger'>*</span>
                              </label>
                              <select
                                className='form-select form-select-sm'
                                value={form.invoiceType || ''}
                                onChange={e =>
                                  handleInvoiceChange(id, 'invoiceType', e.target.value)
                                }
                              >
                                <option value=''>Select</option>
                                <option value='PROFORMA'>Normal Invoice</option>
                                <option value='EINVOICE'>E-Invoice</option>
                              </select>
                            </div>

                            {/* Supply Type */}
                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Supply Type <span className='text-danger'>*</span>
                              </label>
                              <select
                                className='form-select form-select-sm'
                                value={form.supplyType || ''}
                                onChange={e =>
                                  handleInvoiceChange(id, 'supplyType', e.target.value)
                                }
                              >
                                <option value=''>Select</option>
                                <option value='B2B'>B2B</option>
                                <option value='B2C'>B2C</option>
                                <option value='CRN'>Credit Note</option>
                                <option value='SEZWP'>SEZ Invoice with Payment of Tax</option>
                                <option value='SEZWOP'>SEZ Invoice without Payment of Tax</option>
                                <option value='EXPWOP'>Export Invoice without Payment of Tax</option>
                              </select>
                            </div>

                            {/* Buyer GSTIN */}
                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Buyer GSTIN <span className='text-danger'>*</span>
                              </label>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                value={form.supplyType === 'EXPWOP' ? 'URP' : (form.buyerGstin || '')}
                                onChange={e =>
                                  handleInvoiceChange(id, 'buyerGstin', e.target.value)
                                }
                                readOnly={form.supplyType === 'EXPWOP'}
                                placeholder='Enter Buyer GSTIN'
                              />
                            </div>

                            {/* Buyer POS */}
                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Buyer POS <span className='text-danger'>*</span>
                              </label>
                              <input
                                type='text'
                                maxLength={2}
                                className='form-control form-control-sm'
                                value={form.supplyType === 'EXPWOP' ? '96' : (form.buyerPos || '')}
                                onChange={e =>
                                  handleInvoiceChange(id, 'buyerPos', e.target.value)
                                }
                                readOnly={form.supplyType === 'EXPWOP'}
                                placeholder='e.g. 27'
                              />
                            </div>
                          </div>

                          {/* ===== ROW 2 ===== */}
                          <div className='row mt-2'>
                            {/* Buyer Location */}
                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Buyer Location <span className='text-danger'>*</span>
                              </label>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                value={form.buyerLocation || ''}
                                onChange={e =>
                                  handleInvoiceChange(id, 'buyerLocation', e.target.value)
                                }
                                placeholder='Enter Buyer Location'
                              />
                            </div>

                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Buyer PIN <span className='text-danger'>*</span>
                              </label>
                              <input
                                type='number'
                                className='form-control form-control-sm'
                                value={form.buyerPin || ''}
                                onChange={e =>
                                  handleInvoiceChange(id, 'buyerPin', e.target.value)
                                }
                                placeholder='Enter Buyer PIN'
                              />
                            </div>

                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Ship To GSTIN <span className='text-danger'>*</span>
                              </label>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                value={form.supplyType === 'EXPWOP' ? 'URP' : (form.shipToGstin || '')}
                                onChange={e =>
                                  handleInvoiceChange(id, 'shipToGstin', e.target.value)
                                }
                                readOnly={form.supplyType === 'EXPWOP'}
                                placeholder='Enter Ship To GSTIN'
                              />
                            </div>

                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Ship To Name <span className='text-danger'>*</span>
                              </label>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                value={form.shipToLegalName || ''}
                                onChange={e =>
                                  handleInvoiceChange(id, 'shipToLegalName', e.target.value)
                                }
                                placeholder='Enter Ship To Name'
                              />
                            </div>
                          </div>

                          {/* ===== ROW 3 ===== */}
                          <div className='row mt-2'>
                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Ship To Address <span className='text-danger'>*</span>
                              </label>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                value={form.shipToAddress || ''}
                                onChange={e =>
                                  handleInvoiceChange(id, 'shipToAddress', e.target.value)
                                }
                                placeholder='Enter Ship To Address'
                              />
                            </div>

                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Ship To Location <span className='text-danger'>*</span>
                              </label>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                value={form.shipToLocation || ''}
                                onChange={e =>
                                  handleInvoiceChange(id, 'shipToLocation', e.target.value)
                                }
                                placeholder='Enter Ship To Location'
                              />
                            </div>

                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Ship To PIN <span className='text-danger'>*</span>
                              </label>
                              <input
                                type='number'
                                className='form-control form-control-sm'
                                value={form.shipToPin || ''}
                                onChange={e =>
                                  handleInvoiceChange(id, 'shipToPin', e.target.value)
                                }
                                placeholder='Enter Ship To PIN'
                              />
                            </div>

                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Ship To State Code <span className='text-danger'>*</span>
                              </label>
                              <input
                                type='text'
                                maxLength={2}
                                className='form-control form-control-sm'
                                value={form.supplyType === 'EXPWOP' ? '96' : (form.shipToStateCode || '')}
                                onChange={e =>
                                  handleInvoiceChange(id, 'shipToStateCode', e.target.value)
                                }
                                readOnly={form.supplyType === 'EXPWOP'}
                                placeholder={
                                  form.supplyType === 'EXPWOP'
                                    ? 'Other Territory (Export)'
                                    : 'e.g. 27'
                                }
                              />
                            </div>
                          </div>

                          {/* ===== ROW 4 ===== */}
                          <div className='row mt-2'>
                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Dispatch Document No
                              </label>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                value={form.dispatchDocumentNumber || ''}
                                onChange={e =>
                                  handleInvoiceChange(id, 'dispatchDocumentNumber', e.target.value)
                                }
                                placeholder='Enter Dispatch Document No'
                              />
                            </div>
                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Dispatched Through
                              </label>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                value={form.dispatchedThrough || ''}
                                onChange={e =>
                                  handleInvoiceChange(id, 'dispatchedThrough', e.target.value)
                                }
                                placeholder='Enter Carrier / Courier'
                              />
                            </div>

                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Other References
                              </label>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                value={form.otherReferences || ''}
                                onChange={e =>
                                  handleInvoiceChange(id, 'otherReferences', e.target.value)
                                }
                                placeholder='Enter Other References'
                              />
                            </div>

                            <div className='col-md-3 mb-2'>
                              <label className='form-label mb-1'>
                                Vehicle Number
                              </label>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                value={form.vehicleNumber || ''}
                                onChange={e =>
                                  handleInvoiceChange(id, 'vehicleNumber', e.target.value)
                                }
                                placeholder='Enter Vehicle Number'
                              />
                            </div>
                          </div>

                          {/* ===== EXPORT FIELDS (EXPWOP only) ===== */}
                          {form.supplyType === 'EXPWOP' && (
                            <>
                              {/* ROW E1 */}
                              <div className='row mt-2'>
                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    Destination Country Code <span className='text-danger'>*</span>
                                  </label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.destinationCountryCode || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'destinationCountryCode', e.target.value)
                                    }
                                    placeholder='e.g., US'
                                  />
                                </div>

                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    Country of Origin <span className='text-danger'>*</span>
                                  </label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.countryOfOrigin || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'countryOfOrigin', e.target.value)
                                    }
                                    placeholder='e.g., India'
                                  />
                                </div>

                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    Port of Loading <span className='text-danger'>*</span>
                                  </label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.portOfLoading || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'portOfLoading', e.target.value)
                                    }
                                    placeholder='Enter Port of Loading'
                                  />
                                </div>

                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    Port of Discharge <span className='text-danger'>*</span>
                                  </label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.portOfDischarge || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'portOfDischarge', e.target.value)
                                    }
                                    placeholder='Enter Port of Discharge'
                                  />
                                </div>
                              </div>

                              {/* ROW E2 */}
                              <div className='row mt-2'>
                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    Terms of Delivery <span className='text-danger'>*</span>
                                  </label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.termsOfDelivery || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'termsOfDelivery', e.target.value)
                                    }
                                    placeholder='e.g., FOB, CIF'
                                  />
                                </div>

                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    Letter of Credit No
                                  </label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.letterOfCreditNo || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'letterOfCreditNo', e.target.value)
                                    }
                                    placeholder='Enter LC Number'
                                  />
                                </div>

                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    Letter of Credit Date
                                  </label>
                                  <input
                                    type='date'
                                    className='form-control form-control-sm'
                                    value={form.letterOfCreditDate || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'letterOfCreditDate', e.target.value)
                                    }
                                  />
                                </div>

                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    PI No <span className='text-danger'>*</span>
                                  </label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.piNo || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'piNo', e.target.value)
                                    }
                                    placeholder='Enter PI Number'
                                  />
                                </div>
                              </div>

                              {/* ROW E3 */}
                              <div className='row mt-2'>
                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    PI Date <span className='text-danger'>*</span>
                                  </label>
                                  <input
                                    type='date'
                                    className='form-control form-control-sm'
                                    value={form.piDate || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'piDate', e.target.value)
                                    }
                                  />
                                </div>

                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    Terms of Payment <span className='text-danger'>*</span>
                                  </label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.termsOfPayment || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'termsOfPayment', e.target.value)
                                    }
                                    placeholder='e.g., 30 days net'
                                  />
                                </div>

                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    Details of Payment
                                  </label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.detailsOfPayment || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'detailsOfPayment', e.target.value)
                                    }
                                    placeholder='Enter Payment Details'
                                  />
                                </div>

                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    No of Packages <span className='text-danger'>*</span>
                                  </label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.noOfPackages || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'noOfPackages', e.target.value)
                                    }
                                    placeholder='Enter No of Packages'
                                  />
                                </div>
                              </div>

                              {/* ROW E4 */}
                              <div className='row mt-2'>
                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    Gross Weight <span className='text-danger'>*</span>
                                  </label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.grossWeight || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'grossWeight', e.target.value)
                                    }
                                    placeholder='Enter Gross Weight'
                                  />
                                </div>

                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    Net Weight <span className='text-danger'>*</span>
                                  </label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.netWeight || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'netWeight', e.target.value)
                                    }
                                    placeholder='Enter Net Weight'
                                  />
                                </div>

                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>Marking</label>
                                  <input
                                    type='text'
                                    className='form-control form-control-sm'
                                    value={form.marking || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'marking', e.target.value)
                                    }
                                    placeholder='Enter Marking'
                                  />
                                </div>

                                <div className='col-md-3 mb-2'>
                                  <label className='form-label mb-1'>
                                    Currency Type <span className='text-danger'>*</span>
                                  </label>
                                  <select
                                    className='form-select form-select-sm'
                                    value={form.currencyType || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'currencyType', e.target.value)
                                    }
                                  >
                                    <option value=''>Select Currency</option>
                                    <option value='INR'>INR</option>
                                    <option value='USD'>USD</option>
                                    <option value='YUAN'>YUAN</option>
                                    <option value='RUBLE'>RUBLE</option>
                                    <option value='EURO'>EURO</option>
                                    <option value='POUND'>POUND</option>
                                  </select>
                                </div>
                              </div>

                              {/* ROW E5 - Customer Bank Details */}
                              <div className='row mt-2'>
                                <div className='col-md-12 mb-2'>
                                  <label className='form-label mb-1'>
                                    Customer Bank Details <span className='text-danger'>*</span>
                                  </label>
                                  <textarea
                                    className='form-control form-control-sm'
                                    rows={4}
                                    value={form.customerBankDetails || ''}
                                    onChange={e =>
                                      handleInvoiceChange(id, 'customerBankDetails', e.target.value)
                                    }
                                    placeholder='Enter Customer Bank Details'
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* ===== Line Items Table ===== */}
                  <div className='table-responsive'>
                    <Table
                      className='so-table table table-sm table-bordered mb-0'
                      responsive
                    >
                      <thead className='table-light'>
                        <tr>
                          <th style={{ width: 50 }}>SI</th>
                          <th>Name</th>
                          <th style={{ width: 150 }}>Part Code</th>
                          <th style={{ width: 90 }}>HSN</th>
                          <th style={{ width: 90 }}>UOM</th>
                          <th style={{ width: 90 }}>QTY</th>
                          <th style={{ width: 100 }}>LP</th>
                          <th style={{ width: 110 }}>Discount</th>
                          <th style={{ width: 90 }}>GST</th>
                          <th style={{ width: 140 }}>Rate / Unit</th>
                          <th style={{ width: 140 }}>Line Value</th>
                        </tr>
                      </thead>
                      <tbody>{renderLines(lineItems, id, canEditNarration)}</tbody>
                      <tfoot>
                        <tr>
                          <th colSpan='9' className='text-end'>
                            Subtotal
                          </th>
                          <th colSpan='2'>₹ {money(subTotal)}</th>
                        </tr>
                      </tfoot>
                    </Table>

                    {/* Action buttons */}
                    <div className='d-flex justify-content-end mt-2 gap-2'>
                      {pl?.approvalStatus === 'pending' ? (
                        <>
                          <button
                            className='btn add-btn text-white mr-3'
                            onClick={e => updateStatus(e, pl._id, 'approved')}
                          >
                            Approve
                          </button>
                          <button
                            className='btn btn-danger'
                            onClick={() => {
                              setShowDeclineModal(true)
                              setPlId(pl._id)
                            }}
                          >
                            Reject
                          </button>
                        </>
                      ) : pl?.approvalStatus === 'approved' &&
                        pl?.status !== 'invoiced' ? (
                        <button
                          className='btn add-btn text-white'
                          onClick={() => createInvoice(pl)}
                          disabled={btnLoading && creatingId === id}
                        >
                          {btnLoading && creatingId === id ? (
                            <>
                              <Spinner animation='border' size='sm' />{' '}
                              Creating...
                            </>
                          ) : (
                            'Create Invoice'
                          )}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </AccordionBody>
              </AccordionItem>
            )
          })}
        </Accordion>
      </CardBody>

      {/* ===== Decline Modal ===== */}
      <Modal show={showDeclineModal} onHide={handleCloseDeclineModal} centered>
        <Modal.Header className='bg-red-100' closeButton>
          <Modal.Title>Decline Pack List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='my-8 text-center'>
            <textarea
              className='form-control h-full'
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              placeholder='Enter The Reason for Declining The Sales Order'
              rows={4}
            />
          </div>

          <div className='flex max-sm:flex-col gap-4'>
            <button
              onClick={handleCloseDeclineModal}
              type='button'
              className='px-5 py-2.5 rounded-lg w-full tracking-wide text-gray-800 text-sm border-none outline-none bg-gray-200 hover:bg-gray-300'
            >
              Close
            </button>
            <button
              type='button'
              onClick={e => updateStatus(e, plId, 'rejected')}
              className='px-5 py-2.5 rounded-lg w-full tracking-wide text-white text-sm border-none outline-none bg-red-500 hover:bg-red-600'
            >
              Decline
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default Invoice